// Plan 08-04 — REQ-accessibility-baseline pure helpers.
//
// Three redundant visual identity channels for the PieceLayer (UI-SPEC
// §"Accessibility Contract"):
//   1. SHAPE     — slot 0 = hex, slot 1 = diamond
//   2. POSITION  — see board-layout.ts cellIdxToScreen (vertical flip)
//   3. COLOR     — slot 0 = --color-player-a, slot 1 = --color-player-b
//
// These helpers are PURE (no Pixi dependencies) so the identity invariants
// can be unit-tested in happy-dom without a WebGL canvas.

import { themeTokens } from '../../styles/tokens'
import type { RoleKind } from '../PlayerViewSchema'
import type { CssVarKey } from '../theme/descriptor'

export type PieceShape = 'hex' | 'diamond'

export interface RenderablePiece {
  cellIdx: number
  ownerSlot: 0 | 1
  pieceKind: 'king' | 'blank'
  roleKind: RoleKind | null
}

/** The subset of board-cell fields the piece selector reads. */
interface PieceBearingCell {
  cell_idx: number
  piece_owner_slot: 0 | 1 | null
  piece_kind: 'blank' | 'king' | null
  role_kind: RoleKind | null
}

/**
 * Pure selector — picks the board cells that carry a chess piece and maps them
 * to the props PieceLayer renders. A cell carries a piece iff both
 * `piece_owner_slot` and `piece_kind` are non-null; letter-only Runespring
 * cells have both null and are skipped. The chess and word games share one
 * `board[]` (GDD §5.1 single 6-column board, two views), so the same array
 * feeds both LetterLayer and PieceLayer.
 */
export function selectRenderablePieces(
  board: ReadonlyArray<PieceBearingCell>,
): RenderablePiece[] {
  const pieces: RenderablePiece[] = []
  for (const b of board) {
    if (b.piece_owner_slot === null || b.piece_kind === null) continue
    pieces.push({
      cellIdx: b.cell_idx,
      ownerSlot: b.piece_owner_slot,
      pieceKind: b.piece_kind,
      roleKind: b.role_kind,
    })
  }
  return pieces
}

const DEFAULT_THEME = themeTokens

/** Channel 1 — base shape selector. */
export function pieceShapeForSlot(slot: 0 | 1): PieceShape {
  return slot === 0 ? 'hex' : 'diamond'
}

/**
 * Channel 3 — fill color from the active theme descriptor's css_vars block.
 * Falls back to court-of-runes default values when no descriptor is loaded
 * (the boot-time / unthemed-render path) so the layer always paints a
 * non-equal color pair across slots.
 */
export function pieceColorForSlot(
  slot: 0 | 1,
  cssVars: Record<CssVarKey, string> | null,
): string {
  if (slot === 0) {
    return cssVars?.['--color-player-a'] ?? DEFAULT_THEME.playerA
  }
  return cssVars?.['--color-player-b'] ?? DEFAULT_THEME.playerB
}

/** Glow color (used for stroke / aura), same channel as fill but lighter. */
export function pieceGlowForSlot(
  slot: 0 | 1,
  cssVars: Record<CssVarKey, string> | null,
): string {
  if (slot === 0) {
    return cssVars?.['--color-player-a-glow'] ?? DEFAULT_THEME.playerAGlow
  }
  return cssVars?.['--color-player-b-glow'] ?? DEFAULT_THEME.playerBGlow
}
